import { Component, createEffect, createSignal, Show } from 'solid-js';
import { ContextMenuItemConfig, DataItem, MetaDataConfig } from './interfaces';
import DataItemContextMenu from './DataItemContextMenu';
import { computePosition, shift } from '@floating-ui/dom';
import { ModusIconName } from '@trimble-oss/modus-web-components';
import { Spinner } from '@trimble/ui-components/solidjs';
import { t } from '@trimble/i18n';

export interface DataGroupSectionItemProps {
  id: string;
  dataItem: DataItem;
  metadata: MetaDataConfig;
  visible: boolean;
  loading: boolean;
  deleting: boolean;
  contextMenuItems: ContextMenuItemConfig[];
  onItemVisibilityChange(
    dataType: string,
    dataId: string,
    visible: boolean
  ): void;
  onItemLoadCancel?(dataType: string, dataId: string): void;
}

const DataGroupSectionItem: Component<DataGroupSectionItemProps> = (props) => {
  const gray2 = '#b7b9c3'; // gray 2 tailwind theme
  const gray = '#555'; //
  let menuButtonRef!: HTMLButtonElement;
  let menuRef: HTMLDivElement;
  let itemRef!: HTMLDivElement;
  const dataLabel = (): string =>
    props.metadata.label
      ? String(
          props.dataItem[props.metadata.label] ??
            t('shared.projectDataManager.dataGroupSectionItem.noLabel')
        )
      : t('shared.projectDataManager.dataGroupSectionItem.noLabel');
  const dataSublabel = (): string =>
    props.metadata.sublabel
      ? String(
          props.dataItem[props.metadata.sublabel] ??
            t('shared.projectDataManager.dataGroupSectionItem.noSublabel')
        )
      : '';

  const dataType = (): string => String(props.dataItem[props.metadata.type]);

  // Function to manage data visibility on the map
  const handleDataItemVisibility = (
    e: CustomEvent,
    dataType: string,
    dataId: string,
    visible: boolean
  ): void => {
    e.stopPropagation();
    props.onItemVisibilityChange(dataType, dataId, visible);
  };

  const [showContextMenu, setShowContextMenu] = createSignal(false);

  const handleMenuBtnClick = (): void => {
    setShowContextMenu(!showContextMenu());
  };

  createEffect(() => {
    if (showContextMenu()) {
      void computePosition(menuButtonRef, menuRef!, {
        placement: 'right',
        middleware: [shift()],
      }).then(({ x, y }) => {
        Object.assign(menuRef!.style, {
          left: `${x.toString()}px`,
          top: `${y.toString()}px`,
        });
      });
    }
  });

  const handleCloseContextMenu = (): void => {
    setShowContextMenu(false);
  };

  const handleClickOutside = (event: MouseEvent): void => {
    // The event target when clicking on the menu button after the menu is open
    // evaluates to the trb-civil-3d-viewer element. So instead, we need to check whether
    // the XY of the click is inside the bounding rectangel of the menu button.
    const menuButtonRect = menuButtonRef.getBoundingClientRect();
    const isClickInsideMenuButton =
      event.clientX >= menuButtonRect.left &&
      event.clientX <= menuButtonRect.right &&
      event.clientY >= menuButtonRect.top &&
      event.clientY <= menuButtonRect.bottom;

    if (
      // If the event target is inside the portal container
      menuRef!.contains(event.target as Node) ||
      // Or the portal container is the event target
      (event.target as HTMLElement).shadowRoot?.contains(menuRef!) ||
      isClickInsideMenuButton
    ) {
      return;
    }
    setShowContextMenu(false);
  };

  const handleVisibilityChange = (): void => {
    if (document.hidden) {
      setShowContextMenu(false);
    }
  };

  createEffect(() => {
    if (showContextMenu()) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('visibilitychange', handleVisibilityChange);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    }
  });

  const iconName = (): ModusIconName => {
    return props.visible ? 'visibility_on' : 'visibility_off';
  };

  const handleVisibilityCancel = (): void => {
    props.onItemLoadCancel?.(props.metadata.type, props.id);
  };

  const highlightLabel = (): string => {
    if (props.dataItem['disabled']) return 'text-gray-2';
    return '';
  };

  const disabledReason = (): string => {
    return props.dataItem['disabledReason'] || '';
  };

  const iconColor = (): string => {
    if (props.dataItem['disabled']) return gray2;
    return gray;
  };

  const isProcessingTiles = (): boolean => {
    return (
      (props.dataItem['disabled'] &&
        props.dataItem['disabledReason'] ===
          t('siteVisualization.sidePanelLayout.processingFile')) ||
      false
    );
  };

  return (
    <div
      class="flex flex-row gap-2 items-center py-2 px-4"
      ref={itemRef}
      data-testid={`data-group-section-${props.id}`}
    >
      <Show
        when={props.loading || props.deleting}
        fallback={
          isProcessingTiles() ? (
            <Spinner name={props.id} color={gray2} size={24} />
          ) : (
            <modus-icon
              data-testid={`visibility-icon`}
              size="20"
              color={iconColor()}
              class="cursor-pointer flex grow-0"
              name={iconName()}
              onClick={(e) => {
                if (!props.dataItem['disabled']) {
                  handleDataItemVisibility(
                    e as unknown as CustomEvent,
                    dataType(),
                    props.id,
                    !props.visible
                  );
                }
              }}
            />
          )
        }
      >
        {props.loading && (
          <Spinner
            name={props.id}
            onCancel={handleVisibilityCancel}
            size={24}
          />
        )}
        {
          props.deleting && (
            <Spinner name={props.id} size={24} />
          ) /* Can't handle cancelling delete yet */
        }
      </Show>

      <div class="flex flex-col grow cursor-default min-w-0">
        <modus-tooltip
          text={dataLabel()}
          position="bottom"
          class="break-all w-full"
        >
          <div
            class={`w-full overflow-hidden whitespace-nowrap text-ellipsis text-sm ${highlightLabel()}`}
          >
            {dataLabel()}
          </div>
        </modus-tooltip>
        <div class={`flex grow-0 break-all text-sm ${highlightLabel()}`}>
          {dataSublabel()}
        </div>
        <Show when={props.dataItem['disabled'] && disabledReason() !== ''}>
          <div
            data-testid={`disabled-reason-${props.id}`}
            class={`flex grow-0 break-normal text-sm ${highlightLabel()}`}
          >
            {disabledReason()}
          </div>
        </Show>
      </div>

      <Show when={props.contextMenuItems.length > 0}>
        <button
          ref={menuButtonRef}
          type="button"
          data-testid={'more-vertical-btn-' + props.id}
        >
          <modus-icon
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            color={iconColor()}
            name={'more_vertical'}
            onClick={() => {
              handleMenuBtnClick();
            }}
          />
        </button>
      </Show>
      <DataItemContextMenu
        ref={menuRef!}
        show={showContextMenu()}
        onCloseMenu={handleCloseContextMenu}
        contextMenuItems={props.contextMenuItems}
        dataItem={props.dataItem}
      />
    </div>
  );
};

export default DataGroupSectionItem;
